import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { useAuthorizationRequest, useAnswerAuthorization } from '@/hooks/useAuthorizationRequest'
import { useOrganization } from '@/hooks/useOrganization'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { BrandLockup } from '@/components/BrandLockup'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FormError } from '@/components/ui/form-error'
import { HIGH_RISK_API_TOKEN_SCOPES } from '@lib/enums/api-token-scope.enum'
import { oauthScopeParts, type OAuthScope } from '@lib/enums/oauth-scope.enum'
import type { TenantFeature } from '@lib/enums/tenant-feature.enum'

/**
 * What a scope means, said the way somebody would say it out loud.
 *
 * The wire spelling is `events:read`; nobody should be asked to agree to that.
 * A scope with no entry here is still shown — as its own name — because a
 * silent omission would hide something from the person deciding, which is the
 * one thing this screen must never do. The key is the wire scope, so a typo
 * in one of these names is a compile error rather than a label that never matches.
 */
export const SCOPE_LABELS: Partial<Record<OAuthScope, { title: string; detail: string }>> = {
  'events:read': {
    title: 'See your events',
    detail: "Dates, spaces, guest counts, the people on them, and each plan's furniture and where it stands",
  },
  // "See and change", not "Change". A write grant covers every method including
  // GET, so an application given this can read everything the read scope would
  // have shown it. Labeling it "Change your events" understated what was being
  // agreed to, which is the one thing this screen must never do.
  'events:write': {
    title: 'See and change your events',
    detail:
      'Everything above, plus creating and editing events, their phases and their rentals, and moving, adding and removing furniture on their plans',
  },
  'venues:read': { title: 'See your venues', detail: 'Venues, their spaces and their sizes' },
  'venues:write': {
    title: 'See and change your venues',
    detail: 'Everything above, plus editing venues, spaces and inventory',
  },
  'vendor_approvals:read': {
    title: 'See which vendors you have approved',
    detail: 'Which vendors may serve each venue, and for what',
  },
  'vendor_approvals:write': {
    title: 'See and change which vendors you have approved',
    detail: 'Everything above, plus approving and withdrawing vendors at your venues',
  },
  'furniture:read': { title: 'See your furniture catalog', detail: 'Items and saved arrangements' },
  'furniture:write': {
    title: 'See and change your furniture catalog',
    detail: 'Everything above, plus editing items and saved arrangements',
  },
  'people:read': { title: 'See the people in your organization', detail: 'Staff and clients' },
  // A vendor's own company: what its connection reads and changes about itself.
  'vendor_self:read': {
    title: 'See your company',
    detail:
      'Its details, the services it offers, its packages, the venues it works with, its furniture library, and the quote requests and reviews it receives',
  },
  'vendor_self:write': {
    title: 'See and change your company',
    detail: 'Everything above, plus editing those details, services and packages, your furniture library, and asking to serve venues',
  },
}

/**
 * What `venues` reaches for a company with Marketing: the public pricing pages
 * too, which a connected assistant can read and edit (`get_marketing_page`,
 * `update_marketing_page`). Said only to such a company, because Marketing is
 * hidden from one without it rather than sold to it (`offMode: 'hidden'`).
 */
const WITH_MARKETING: Partial<Record<OAuthScope, string>> = {
  'venues:read': 'Venues, their spaces and their sizes, and their public pricing pages',
  'venues:write': 'Everything above, plus editing venues, spaces, inventory and the public pricing pages',
}

/** What a scope means for this person's organization, given the features it has. */
export function scopeLabel(
  scope: OAuthScope,
  features: readonly TenantFeature[],
): { title: string; detail: string } | undefined {
  const label = SCOPE_LABELS[scope]
  const detail = features.includes('marketing') ? WITH_MARKETING[scope] : undefined
  return label === undefined || detail === undefined ? label : { ...label, detail }
}

/**
 * The scopes on this screen that would let the application change something.
 *
 * Exported and tested because getting it wrong is what made the first version
 * of this screen contradict itself: it offered "let it look, but not change
 * anything" beside a list of things that only look, so ticking "See your
 * events" read as though it had granted changes that had to be taken back.
 */
export function writesAmong(scopes: Iterable<OAuthScope>): OAuthScope[] {
  return [...scopes].filter((scope) => oauthScopeParts(scope).write)
}

/** Whether a scope is one the token screen already treats as consequential. */
function isHighRisk(scope: OAuthScope): boolean {
  return HIGH_RISK_API_TOKEN_SCOPES.includes(oauthScopeParts(scope).scope)
}

/**
 * The consent screen: the one page in this flow a person actually reads.
 *
 * It answers three questions the generic OAuth screen does not — whose data,
 * what the application may do with it, and that the answer follows them rather
 * than being frozen at this moment. See `docs/mcp-server.md`.
 *
 * **Approval is not all-or-nothing.** Every scope can be refused individually
 * and the whole connection can be held to reading. That matters more here than
 * it would elsewhere: the risk this screen exists to manage is an assistant
 * acting on text it read, and event names, client notes and imported rows are
 * all content this system did not author. Refusing the write half is the one
 * thing a person can do about that themselves.
 *
 * It sits behind `ProtectedRoute`, so somebody arriving from an application
 * signs in first — usually with an emailed link, since the people this is for
 * use the app a few times a year.
 */
export function ConnectPage() {
  const { user } = useAuth()
  const [params] = useSearchParams()
  const requestId = params.get('request') ?? ''

  const { data: request, isPending, error: loadError } = useAuthorizationRequest(requestId)
  const answer = useAnswerAuthorization(requestId)
  // Which features to describe the scopes with. Waited for, so the list a person
  // agrees to is never the shorter one that was on screen a moment before.
  const organization = useOrganization()
  const features = organization.data?.features ?? []

  const [chosen, setChosen] = React.useState<Set<OAuthScope> | null>(null)
  const [readOnly, setReadOnly] = React.useState<boolean>(false)

  /**
   * Everything the application asked for starts ticked.
   *
   * The person is agreeing to what it requested, so unticking is the deliberate
   * act rather than ticking. Held as null until the request arrives so the first
   * render cannot mistake "not loaded" for "nothing chosen" and disable the
   * button under somebody who has chosen nothing yet because there is nothing
   * to choose from.
   */
  const scopes = React.useMemo(() => chosen ?? new Set(request?.scopes ?? []), [chosen, request])

  function toggle(scope: OAuthScope): void {
    const next = new Set(scopes)
    if (next.has(scope)) next.delete(scope)
    else next.add(scope)
    setChosen(next)
  }

  function decide(which: 'approve' | 'deny'): void {
    answer.mutate(
      which === 'deny' ? { answer: 'deny' } : { answer: 'approve', input: { scopes: [...scopes], readOnly } },
      {
        onSuccess: (decision) => {
          // Leaving this app for the one that sent them. A full navigation
          // rather than a router push: the destination is not ours.
          window.location.assign(decision.redirectTo)
        },
      },
    )
  }

  const error =
    requestId === ''
      ? 'That link is missing the request it was meant to carry.'
      : loadError !== null
        ? requestErrorMessage(loadError)
        : answer.error !== null
          ? requestErrorMessage(answer.error)
          : null

  if (error !== null && request === undefined) {
    return (
      <Shell>
        <CardHeader>
          <CardTitle>That request can’t be answered</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-ink-muted">{error}</p>
          <p className="text-sm text-ink-muted">
            Start the connection again from the application that sent you here.
          </p>
        </CardContent>
      </Shell>
    )
  }

  if (isPending || request === undefined || organization.isLoading) {
    return (
      <Shell>
        <CardContent className="flex items-center gap-3 py-10 text-sm text-ink-muted">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Checking that request…
        </CardContent>
      </Shell>
    )
  }

  // A role that cannot hold a grant is told so instead of being shown a form.
  // Showing one and refusing it afterwards is what produced "approve at least
  // one thing" to somebody who had ticked everything on offer.
  if (!request.canConnect) {
    return (
      <Shell>
        <CardHeader>
          <CardTitle>This account can’t connect applications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-ink-muted">{request.refusal}</p>
          <p className="text-sm text-ink-muted">
            Nothing has been shared and nothing has changed.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              decide('deny')
            }}
            disabled={answer.isPending}
          >
            Tell {request.clientName} no
          </Button>
        </CardContent>
      </Shell>
    )
  }

  const nothingChosen = scopes.size === 0
  // Whether "let it look but not change anything" has anything to turn off.
  // Offering it beside a list of things that only look reads as though the list
  // grants changes — which is what made the two controls contradict each other.
  const canWrite = writesAmong(scopes).length > 0

  return (
    <Shell>
      <CardHeader>
        <CardTitle>
          Connect <span className="font-semibold">{request.clientName}</span>?
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-ink-muted">
          It will act as <span className="font-medium text-ink">{user?.name}</span> and can
          reach exactly what you can — no more. If your venues or your role change, what it can
          reach changes with them.
        </p>

        <fieldset className="space-y-3">
          <legend className="sr-only">What this application may do</legend>
          {request.scopes.map((scope) => {
            const label = scopeLabel(scope, features)
            return (
              <label
                key={scope}
                className="flex cursor-pointer items-start gap-2 rounded-md border border-line p-3 text-sm"
              >
                <input
                  type="checkbox"
                  checked={scopes.has(scope)}
                  onChange={() => {
                    toggle(scope)
                  }}
                  aria-describedby={`${scope}-detail`}
                  className="mt-1 h-4 w-4 rounded border-line"
                />
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium text-ink">{label?.title ?? scope}</span>
                    {isHighRisk(scope) ? <Badge tone="warning">Sensitive</Badge> : null}
                  </span>
                  <span id={`${scope}-detail`} className="block text-xs text-ink-muted">
                    {label?.detail ?? 'Access to this part of your organization'}
                  </span>
                </span>
              </label>
            )
          })}
        </fieldset>

        {canWrite ? (
          <label className="flex cursor-pointer items-start gap-2 rounded-md border border-line p-3 text-sm">
            <input
              type="checkbox"
              checked={readOnly}
              onChange={(e) => {
                setReadOnly(e.target.checked)
              }}
              aria-describedby="read-only-detail"
              className="mt-1 h-4 w-4 rounded border-line"
            />
            <span className="min-w-0">
              <span className="block font-medium text-ink">
                Let it look, but not change anything
              </span>
              <span id="read-only-detail" className="block text-xs text-ink-muted">
                Keeps everything ticked above, but turns off the changing half. The safer
                choice — you can connect again later if you want it to make changes.
              </span>
            </span>
          </label>
        ) : (
          <p className="rounded-md border border-line bg-surface-sunken/40 p-3 text-xs text-ink-muted">
            Everything here is read-only. Nothing you tick above lets this application change
            anything.
          </p>
        )}

        {error !== null ? <FormError>{error}</FormError> : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => {
              decide('approve')
            }}
            disabled={answer.isPending || nothingChosen}
          >
            {answer.isPending ? <Loader2 className="mr-2 size-4 animate-spin" aria-hidden /> : null}
            Connect
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              decide('deny')
            }}
            disabled={answer.isPending}
          >
            Don’t connect
          </Button>
        </div>

        {nothingChosen ? (
          <p className="text-xs text-ink-muted">
            Nothing is ticked, so there is nothing to agree to. Tick something, or decline.
          </p>
        ) : null}

        <p className="text-xs text-ink-muted">
          You can end this connection at any time from your profile, where it will be listed with
          your other access tokens.
        </p>
      </CardContent>
    </Shell>
  )
}

/** The unchromed frame these pages share: no nav, because there is one decision here. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center bg-surface-sunken/30 p-4">
      <div className="w-full max-w-lg space-y-6">
        <BrandLockup className="justify-center" />
        <Card size="lg">{children}</Card>
      </div>
    </div>
  )
}
